import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';

export interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: string;
}

export interface TableData {
  [key: string]: any;
  children?: TableData[];
}

interface GenericTableProps {
  data: TableData[];
  columns: TableColumn[];
  showHierarchy?: boolean;
  getRowBackgroundColor?: (row: TableData) => string;
  getRowTextColor?: (row: TableData) => string;
  getCellBackgroundColor?: (row: TableData, columnKey: string) => string;
  getCellTextColor?: (row: TableData, columnKey: string) => string;
}

export const GenericTable: React.FC<GenericTableProps> = ({
  data,
  columns,
  showHierarchy = false,
  getRowBackgroundColor = () => '#ffffff',
  getRowTextColor = () => '#000000',
  getCellBackgroundColor = (row, columnKey) => getRowBackgroundColor(row),
  getCellTextColor = (row, columnKey) => getRowTextColor(row)
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItem = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const isExpanded = (itemId: string) => expandedItems.has(itemId);

  const getIndentation = (level: number) => {
    return `${level * 24}px`;
  };

  const renderHierarchicalRow = (row: TableData, level: number = 0) => {
    const hasChildren = row.children && row.children.length > 0;
    const isExpandedRow = isExpanded(row.id || String(row.key) || '');

    return (
      <React.Fragment key={row.id || row.key}>
        <TableRow 
          sx={{ 
            backgroundColor: getRowBackgroundColor(row),
            color: getRowTextColor(row),
            '&:hover': {
              backgroundColor: `${getRowBackgroundColor(row)}DD`,
            }
          }}
        >
          {columns.map((column, colIndex) => (
            <TableCell 
              key={column.key}
              align={column.align}
              sx={{ 
                fontSize: '0.75rem',
                p: 1,
                borderRight: colIndex < columns.length - 1 ? '1px solid #e0e0e0' : 'none',
                backgroundColor: getCellBackgroundColor(row, column.key),
                color: getCellTextColor(row, column.key),
                ...(colIndex === 0 && showHierarchy && {
                  pl: `calc(${getIndentation(level)} + ${hasChildren ? '0' : '28px'})`
                })
              }}
            >
              {colIndex === 0 && showHierarchy && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {hasChildren && (
                    <IconButton
                      size="small"
                      onClick={() => toggleItem(row.id || String(row.key) || '')}
                      sx={{ 
                        padding: '2px',
                        marginRight: '4px',
                        color: getRowTextColor(row),
                      }}
                    >
                      {isExpandedRow ? 
                        <KeyboardArrowDown fontSize="small" /> : 
                        <KeyboardArrowRight fontSize="small" />
                      }
                    </IconButton>
                  )}
                  {!hasChildren && <Box sx={{ width: '28px' }} />}
                  {row[column.key]}
                </Box>
              )}
              {colIndex !== 0 && row[column.key]}
            </TableCell>
          ))}
        </TableRow>

        {hasChildren && isExpandedRow && row.children!.map(child => 
          renderHierarchicalRow(child, level + 1)
        )}
      </React.Fragment>
    );
  };

  const renderFlatRow = (row: TableData, index: number) => (
    <TableRow 
      key={row.id || index}
      sx={{ 
        backgroundColor: getRowBackgroundColor(row),
        color: getRowTextColor(row),
        '&:hover': {
          backgroundColor: `${getRowBackgroundColor(row)}DD`,
        }
      }}
    >
      {columns.map((column, colIndex) => (
        <TableCell 
          key={column.key}
          align={column.align}
          sx={{ 
            fontSize: '0.75rem',
            p: 1,
            borderRight: colIndex < columns.length - 1 ? '1px solid #e0e0e0' : 'none',
            backgroundColor: getCellBackgroundColor(row, column.key),
            color: getCellTextColor(row, column.key),
          }}
        >
          {row[column.key]}
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        maxHeight: 400,
        border: '1px solid #e0e0e0',
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ 
            backgroundColor: '#f5f5f5',
            '& th': {
              borderRight: '1px solid #e0e0e0',
              borderBottom: '2px solid #e0e0e0',
              '&:last-child': { borderRight: 'none' }
            }
          }}>
            {columns.map(column => (
              <TableCell 
                key={column.key}
                align={column.align}
                sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.75rem', 
                  p: 1,
                  width: column.width 
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {showHierarchy 
            ? data.map(row => renderHierarchicalRow(row))
            : data.map((row, index) => renderFlatRow(row, index))
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
};